import { client } from "../api/client.js";
import { renderPageHeader } from "../ui/pageHeader.js";

export class AnalyticsPage {
  constructor() {
    this.state = {
      // Overview State
      loading: true,
      error: null,
      data: null,
      range: '30d',
      platform: 'all',

      // Follower Growth State
      followersLoading: true,
      followersError: null,
      followersData: null,
      followersRange: localStorage.getItem('analytics_followers_range') || '30d',
      followersProvider: localStorage.getItem('analytics_followers_provider') || 'all',
      snapshotRunning: false,
      lastRunResults: null,

      // Content Stats State
      contentStatsLoading: true,
      contentStatsError: null,
      contentStats: null,

      // Performance State
      perfLoading: true,
      perfError: null,
      perfData: null,
      perfTrends: null,
      perfPosts: null,
      perfPostsTotal: 0,
      perfPostsPage: 1,
      perfSort: 'engagement',
      perfSyncing: false,
      // Phase 37 — Refresh Now UX state
      perfLastSyncedAt: null,    // ISO string from /performance/last-synced
      perfNextAllowedAt: null,   // ISO string of when the cooldown expires
      perfRefreshMessage: null,  // transient toast text after refresh
      perfRefreshMessageKind: null, // 'success' | 'rate-limited' | 'error'

      // Team Stats State
      teamLoading: false,
      teamError: null,
      teamStats: null,

      // Phase 31E: Per-post drill-down drawer
      perfDrawerPost: null
    };
    this.abortController = null;
    this.followersAbortController = null;
    this.perfAbortController = null;
    this.activeTab = 'planning';
  }

  async mount(el) {
    this.el = el;

    // Refetch derived analytics whenever content mutates (delete, status change,
    // save draft, etc.) so the Analytics page stays in sync without manual reload.
    this._onContentChanged = (ev) => {
      // Coalesce bursts: debounce refetch by ~350ms
      clearTimeout(this._refetchTimer);
      this._refetchTimer = setTimeout(() => {
        this.fetchOverview();
        this.fetchContentStats();
        if (this.activeTab === 'performance') this.fetchPerformance();
      }, 350);
    };
    window.addEventListener('heist:content-changed', this._onContentChanged);

    // Refetch on tab focus (user switched away, came back — data may be stale).
    this._onVisibility = () => {
      if (document.visibilityState === 'visible') {
        this.fetchOverview();
        this.fetchContentStats();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibility);

    // Use event delegation for all analytics interactions
    this.el.addEventListener('change', (e) => {
      const id = e.target.id;
      if (id === 'analytics-range') this.handleRangeChange(e);
      if (id === 'analytics-platform') this.handlePlatformChange(e);
      if (id === 'analytics-followers-range') this.handleFollowersRangeChange(e);
      if (id === 'analytics-followers-provider') this.handleFollowersProviderChange(e);
      if (id === 'perf-sort') this.handlePerfSortChange(e);
    });

    this.el.addEventListener('click', (e) => {
      // Phase 31D: Coming-soon chips — toast then swallow (still used elsewhere)
      const comingChip = e.target.closest('[data-coming-soon]');
      if (comingChip) {
        e.preventDefault();
        this._showComingSoonToast();
        return;
      }
      // Phase 31.5C: Website/Blog filter chips — toggle platform filter + refetch
      const siteChip = e.target.closest('[data-site-platform]');
      if (siteChip) {
        e.preventDefault();
        const target = siteChip.dataset.sitePlatform;
        const nextPlatform = this.state.platform === target ? 'all' : target;
        this.setState({ platform: nextPlatform, perfPostsPage: 1 });
        this.fetchOverview();
        this.fetchPerformance();
        return;
      }
      // Phase 31G: Export PDF — open the report URL in a new tab
      const pdfBtn = e.target.closest('[data-action="export-pdf"]');
      if (pdfBtn) {
        e.preventDefault();
        const days = this.state.range === '7d' ? 7 : this.state.range === '90d' ? 90 : 30;
        window.open(client.engagementReportPdfUrl({ days }), '_blank');
        return;
      }
      // Phase 31E: Close drawer — handle before row click so X doesn't re-open
      if (e.target.closest('[data-engagement-drawer-close]')) {
        this.setState({ perfDrawerPost: null });
        return;
      }
      // Phase 31E: Per-post row click → open drawer
      const row = e.target.closest('.post-engagement-row');
      if (row) {
        const postId = row.dataset.postId;
        const post = (this.state.perfPosts || []).find(p => String(p.id) === String(postId));
        if (post) this.setState({ perfDrawerPost: post });
        return;
      }
      const tab = e.target.closest('[data-analytics-tab]');
      if (tab) {
        const newTab = tab.dataset.analyticsTab;
        if (newTab !== this.activeTab) {
          this.activeTab = newTab;
          this.render();
          if (newTab === 'performance' && !this.state.perfData) this.fetchPerformance();
          if (newTab === 'team' && !this.state.teamStats) this.fetchTeamStats();
        }
        return;
      }
      const cta = e.target.closest('[data-nav]');
      if (cta) {
        e.preventDefault();
        window.location.hash = '#' + cta.dataset.nav;
        return;
      }
      const btn = e.target.closest('button');
      if (!btn) return;
      const id = btn.id;
      if (id === 'btn-retry-overview') this.fetchOverview();
      if (id === 'btn-retry-followers') this.fetchFollowers();
      if (id === 'btn-run-snapshot') this.handleRunSnapshot();
      if (id === 'btn-perf-sync') this.handlePerfSync();
      if (id === 'btn-retry-perf') this.fetchPerformance();
      if (id === 'btn-perf-prev') this.handlePerfPageChange(-1);
      if (id === 'btn-perf-next') this.handlePerfPageChange(1);
      if (id === 'analytics-export-csv') this.handleExportCSV();
    });

    this.render();
    this.fetchOverview();
    this.fetchFollowers();
    this.fetchContentStats();
  }

  async fetchOverview() {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setState({ loading: true, error: null });

    try {
      const { range, platform } = this.state;
      const data = await client.getAnalyticsOverview({ range, platform });

      if (!data.ok) {
        throw new Error(data.message || 'Failed to load analytics overview');
      }

      this.setState({ loading: false, data });
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.setState({
        loading: false,
        error: {
          message: err.message,
          requestId: err.requestId
        }
      });
    }
  }

  async fetchFollowers() {
    if (this.followersAbortController) this.followersAbortController.abort();
    this.followersAbortController = new AbortController();

    this.setState({ followersLoading: true, followersError: null });

    try {
      const { followersRange, followersProvider } = this.state;
      const data = await client.getAnalyticsFollowersSeries({ range: followersRange, provider: followersProvider });

      if (!data.ok) {
        throw new Error(data.message || 'Failed to load follower series');
      }

      this.setState({ followersLoading: false, followersData: data });
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.setState({
        followersLoading: false,
        followersError: {
          message: err.message,
          requestId: err.requestId
        }
      });
    }
  }

  async fetchContentStats() {
    this.setState({ contentStatsLoading: true, contentStatsError: null });
    try {
      const res = await client.getAnalyticsContentStats();
      if (!res.ok) throw new Error(res.message || 'Failed to load content stats');
      this.setState({ contentStatsLoading: false, contentStats: res.data });
    } catch (err) {
      this.setState({ contentStatsLoading: false, contentStatsError: err.message });
    }
  }

  async fetchPerformance() {
    if (this.perfAbortController) this.perfAbortController.abort();
    this.perfAbortController = new AbortController();

    this.setState({ perfLoading: true, perfError: null });

    try {
      const { range, platform, perfSort, perfPostsPage } = this.state;
      const [perfRes, trendsRes, postsRes] = await Promise.all([
        client.getAnalyticsPerformance({ range, platform }),
        client.getAnalyticsPerformanceTrends({ range, platform }),
        client.getAnalyticsPerformancePosts({ range, platform, sort: perfSort, page: perfPostsPage })
      ]);

      if (!perfRes.ok) throw new Error(perfRes.error || 'Failed to load performance data');

      this.setState({
        perfLoading: false,
        perfData: perfRes,
        perfTrends: trendsRes.ok ? trendsRes.series : [],
        perfPosts: postsRes.ok ? postsRes.posts : [],
        perfPostsTotal: postsRes.ok ? postsRes.total : 0
      });
      // Phase 37 — pull the last-synced timestamp lazily so the Refresh Now
      // button shows accurate freshness on first paint, not just after a
      // manual click. Errors are non-fatal — the UI just falls back to "—".
      this.fetchLastSynced();
      // R6 Stage 4 — per-platform sync health for the "Engagement sources"
      // strip. Non-fatal; strip simply doesn't render without data.
      client.getAnalyticsSyncStatus().then((r) => {
        if (r?.ok) this.setState({ perfSyncStatus: r.platforms || [] });
      }).catch(() => {});
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.setState({ perfLoading: false, perfError: err.message });
    }
  }

  async handlePerfSync() {
    if (this.state.perfSyncing) return;
    this.setState({ perfSyncing: true, perfRefreshMessage: null });
    try {
      const res = await client.syncAnalyticsPerformance();
      // Phase 37 — distinguish "refreshed" vs "rate-limited (still in cooldown)"
      // so the UI can give a precise message instead of a generic spinner stop.
      if (res?.triggered === false) {
        this.setState({
          perfSyncing: false,
          perfNextAllowedAt: res.next_allowed_at || null,
          perfLastSyncedAt: res.last_synced_at || this.state.perfLastSyncedAt,
          perfRefreshMessage: 'Already up to date \u2014 refresh available again shortly.',
          perfRefreshMessageKind: 'rate-limited',
        });
      } else {
        this.setState({
          perfSyncing: false,
          perfNextAllowedAt: res?.next_allowed_at || null,
          perfLastSyncedAt: res?.last_synced_at || new Date().toISOString(),
          perfRefreshMessage: res?.synced > 0
            ? `Refreshed \u2014 ${res.synced} post${res.synced === 1 ? '' : 's'} updated.`
            : 'Refreshed \u2014 no new engagement since last check.',
          perfRefreshMessageKind: 'success',
        });
      }
      await this.fetchPerformance();
      // Auto-clear the toast after 4s.
      clearTimeout(this._perfRefreshMsgTimer);
      this._perfRefreshMsgTimer = setTimeout(() => {
        if (this.state.perfRefreshMessage) {
          this.setState({ perfRefreshMessage: null, perfRefreshMessageKind: null });
        }
      }, 4000);
    } catch (err) {
      this.setState({
        perfSyncing: false,
        perfError: err.message,
        perfRefreshMessage: 'Refresh failed \u2014 try again in a moment.',
        perfRefreshMessageKind: 'error',
      });
    }
  }

  // Phase 37 — fetch the last-synced timestamp on mount + after page refresh,
  // so the "Refreshed X ago" line is accurate even before the user clicks.
  async fetchLastSynced() {
    try {
      const res = await client.getAnalyticsLastSynced();
      if (res?.ok) {
        this.setState({
          perfLastSyncedAt: res.last_synced_at,
          perfNextAllowedAt: res.next_allowed_at,
        });
      }
    } catch (_) { /* non-fatal — UI just shows "—" */ }
  }

  // Returns "12 minutes ago", "2 hours ago", etc. Returns null if never synced.
  _formatRelativeTime(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return null;
    const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (sec < 30) return 'just now';
    if (sec < 60) return `${sec} sec ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    return `${day} day${day === 1 ? '' : 's'} ago`;
  }

  handlePerfSortChange(e) {
    this.setState({ perfSort: e.target.value, perfPostsPage: 1 });
    this.fetchPerformance();
  }

  handlePerfPageChange(delta) {
    const newPage = Math.max(1, this.state.perfPostsPage + delta);
    this.setState({ perfPostsPage: newPage });
    this.fetchPerformance();
  }

  setState(newState) {
    this.state = { ...this.state, ...newState };
    this.render();
  }

  // --- Overview Handlers ---
  handleRangeChange(e) {
    this.setState({ range: e.target.value, perfPostsPage: 1 });
    this.fetchOverview();
    if (this.activeTab === 'performance') this.fetchPerformance();
    if (this.activeTab === 'team') this.fetchTeamStats();
  }

  handlePlatformChange(e) {
    this.setState({ platform: e.target.value, perfPostsPage: 1 });
    this.fetchOverview();
    if (this.activeTab === 'performance') this.fetchPerformance();
  }

  // --- Follower Growth Handlers ---
  handleFollowersRangeChange(e) {
    const val = e.target.value;
    localStorage.setItem('analytics_followers_range', val);
    this.setState({ followersRange: val });
    this.fetchFollowers();
  }

  handleFollowersProviderChange(e) {
    const val = e.target.value;
    localStorage.setItem('analytics_followers_provider', val);
    this.setState({ followersProvider: val });
    this.fetchFollowers();
  }

  async handleRunSnapshot() {
    if (this.state.snapshotRunning) return;

    // Clear any error from a previous run so a stale banner doesn't linger
    // over a successful retry.
    this.setState({ snapshotRunning: true, followersRunError: null });

    try {
      const res = await client.runAnalyticsFollowersSnapshot({ provider: this.state.followersProvider });

      // Save results to state
      this.setState({ lastRunResults: res.results, snapshotRunning: false, followersRunError: null });

      // Immediately refresh chart/KPIs
      await this.fetchFollowers();

    } catch (err) {
      this.setState({ snapshotRunning: false, followersRunError: { message: err.message, requestId: err.requestId } });
    }
  }

  handleExportCSV() {
    const { data, range, platform } = this.state;
    if (!data) return;
    const { kpis, breakdownByPlatform } = data;
    const rows = [
      ['Metric', 'Value'],
      ['Range', range],
      ['Platform Filter', platform],
      ['Scheduled (in range)', kpis?.scheduled ?? ''],
      ['Active Days (in range)', kpis?.activeDaysInRange ?? ''],
      ['Drafts Created (in range)', kpis?.drafts ?? ''],
      ['Next 30 Coverage (days)', kpis?.coverageNext30Days ?? ''],
      ['Next 30 Gaps', kpis?.gapCountNext30 ?? ''],
      ['Draft Backlog', kpis?.draftBacklog ?? ''],
      ['Lead Time (days)', kpis?.leadTimeDays ?? ''],
      [],
      ['Platform', 'Scheduled', 'Drafts'],
      ...(breakdownByPlatform || []).map(bp => [bp.platform, bp.scheduled, bp.drafts])
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `heist-analytics-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async unmount() {
    if (this._onContentChanged) {
      window.removeEventListener('heist:content-changed', this._onContentChanged);
      this._onContentChanged = null;
    }
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
    clearTimeout(this._refetchTimer);
    clearTimeout(this._cooldownTimer);
    clearTimeout(this._perfRefreshMsgTimer);
    if (this.abortController) this.abortController.abort();
    if (this.followersAbortController) this.followersAbortController.abort();
    if (this.perfAbortController) this.perfAbortController.abort();
    this.el = null;
  }

  render() {
    if (!this.el) return;

    this.el.innerHTML = `
      <div id="page-analytics" class="analytics-page-root" data-testid="analytics-page">
        ${this.renderOverview()}
        <div class="page-shell page-content">
          <div class="analytics-tabs">
            <button class="analytics-tab${this.activeTab === 'planning' ? ' active' : ''}" data-analytics-tab="planning">Planning</button>
            <button class="analytics-tab${this.activeTab === 'performance' ? ' active' : ''}" data-analytics-tab="performance">Performance</button>
            ${window.__auth__?.workspace_id && ['owner', 'admin'].includes(window.__auth__?.workspace_role) ? `
              <button class="analytics-tab${this.activeTab === 'team' ? ' active' : ''}" data-analytics-tab="team">Team</button>
            ` : ''}
          </div>
          ${this.activeTab === 'planning' ? `
            ${this.renderContentStats()}
            ${this.renderFollowerGrowth()}
          ` : this.activeTab === 'team' ? this.renderTeamTab() : this.renderPerformanceTab()}
        </div>
      </div>
    `;
  }

  renderOverview() {
    const { loading, error, data, range, platform } = this.state;

    // Header (Full width)
    const headerHtml = renderPageHeader({
      eyebrow: "Performance",
      title: "Analytics",
      subtitle: "The score — what's working and what's not.",
      tourPlaylist: 'analytics',
      rightHtml: `
                <div class="analytics-toolbar">
                  <select id="analytics-range" class="filter-select">
                    <option value="7d" ${range === '7d' ? 'selected' : ''}>Last 7 Days</option>
                    <option value="30d" ${range === '30d' ? 'selected' : ''}>Last 30 Days</option>
                    <option value="90d" ${range === '90d' ? 'selected' : ''}>Last 90 Days</option>
                  </select>
                  <select id="analytics-platform" class="filter-select">
                    <option value="all" ${platform === 'all' ? 'selected' : ''}>All Platforms</option>
                    <option value="facebook" ${platform === 'facebook' ? 'selected' : ''}>Facebook</option>
                    <option value="instagram" ${platform === 'instagram' ? 'selected' : ''}>Instagram</option>
                    <option value="x" ${platform === 'x' ? 'selected' : ''}>X</option>
                    <option value="linkedin" ${platform === 'linkedin' ? 'selected' : ''}>LinkedIn</option>
                    <option value="blog" ${platform === 'blog' ? 'selected' : ''}>Blog</option>
                    <option value="website" ${platform === 'website' ? 'selected' : ''}>Website</option>
                    <option value="tiktok" ${platform === 'tiktok' ? 'selected' : ''}>TikTok</option>
                    <option value="youtube" ${platform === 'youtube' ? 'selected' : ''}>YouTube</option>
                    <option value="pinterest" ${platform === 'pinterest' ? 'selected' : ''}>Pinterest</option>
                    <option value="reddit" ${platform === 'reddit' ? 'selected' : ''}>Reddit</option>
                  </select>
                  <button id="analytics-export-csv" class="btn btn-secondary btn-sm" title="Export CSV">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Export
                  </button>
                </div>
      `
    });

    // Wrap the rest of the overview in page-shell page-content
    let overviewContentStart = `<div class="page-shell page-content">`;
    let overviewContentEnd = `</div>`;

    // Content
    let contentHtml = '';


    if (loading && !data) {
      // Skeletons only on FIRST load. Background refetches (tab focus,
      // content-changed events) keep the existing data on screen instead of
      // flashing the whole page back to skeletons.
      contentHtml += `
        <div class="analytics-kpi-grid" data-testid="analytics-loading-skeletons">
           ${Array(6).fill(0).map(() => `
             <div class="analytics-skeleton analytics-skeleton-card"></div>
           `).join('')}
        </div>
        <div class="analytics-two-col">
          <div class="analytics-skeleton analytics-skeleton-chart"></div>
          <div class="analytics-skeleton analytics-skeleton-table"></div>
        </div>
      `;
    } else if (error) {
      contentHtml += `
        <div class="analytics-banner analytics-banner--error" data-testid="analytics-error-banner">
          <div class="analytics-banner-content">
            <h3 class="analytics-banner-title">Failed to load overview</h3>
            <p class="analytics-banner-message">${this._esc(error.message)}</p>
            ${error.requestId ? `<span class="analytics-request-id">Request ID: ${this._esc(error.requestId)}</span>` : ''}
          </div>
          <button id="btn-retry-overview" class="analytics-button analytics-button--secondary">Retry Overview</button>
        </div>
      `;
    } else if (data) {
      const { kpis, coverageNext30, breakdownByPlatform, attention } = data;

      const kpiCard = (title, value, subtext = '', status = '') => {
        let modifier = '';
        if (status === 'success') modifier = 'analytics-kpi-subtext--success';
        else if (status === 'warn') modifier = 'analytics-kpi-subtext--warn';
        else if (status === 'danger' || status === 'danger-bold') modifier = 'analytics-kpi-subtext--danger';

        return `
          <div class="analytics-card analytics-kpi-card" data-testid="kpi-card-${title.toLowerCase().replace(/\s+/g, '-')}">
            <div class="analytics-kpi-label" data-testid="kpi-label">${title}</div>
            <div class="analytics-kpi-value" data-testid="kpi-value">${value}</div>
            ${subtext ? `<div class="analytics-kpi-subtext ${modifier}">${subtext}</div>` : ''}
          </div>
        `;
      };

      contentHtml += `
        <div class="analytics-kpi-grid" data-testid="analytics-kpi-grid">
          ${kpiCard('Scheduled', kpis.scheduled, 'In Range')}
          ${kpiCard('Active Days', kpis.activeDaysInRange, 'In Range')}
          ${kpiCard('Next 30 Coverage', `${kpis.coverageNext30Days}/30`, 'Days Covered')}
          ${kpiCard('Next 30 Gaps', kpis.gapCountNext30, kpis.gapCountNext30 > 0 ? `First: ${kpis.firstGapDateNext30}` : 'No Gaps', kpis.gapCountNext30 > 0 ? 'warn' : 'success')}
          ${kpiCard('Draft Backlog', kpis.draftBacklog, 'Total Available')}
          ${kpiCard('Lead Time', `${kpis.leadTimeDays}d`, 'Schedule Runway', kpis.leadTimeDays < 7 ? 'danger-bold' : '')}
        </div>
       `;

      // Charts Row (Coverage + Breakdown)
      contentHtml += `<div class="analytics-two-col">`;

      // Coverage Chart
      contentHtml += `
         <div class="analytics-card" data-testid="analytics-coverage">
           <div class="analytics-card-header">
             <h3 class="analytics-card-title">Coverage (Next 30 Days)</h3>
           </div>
           <div class="analytics-chart analytics-chart--coverage">
              <div class="analytics-coverage-bars">
                ${coverageNext30.map(day => {
        const heightPct = Math.min(100, (day.scheduled / 5) * 100);
        const isGap = day.scheduled === 0;
        // Inline styles only for dynamic height
        return `<div class="analytics-coverage-bar ${isGap ? 'analytics-coverage-bar--gap' : ''}" style="height: ${isGap ? '4px' : heightPct + '%'}" title="${day.date}: ${day.scheduled}"></div>`;
      }).join('')}
              </div>
           </div>
           <div class="analytics-chart-legend">
             <span>Today</span>
             <span>+30 Days</span>
           </div>
         </div>
       `;

      // Breakdown Table — Phase 38 polish:
      //  • Subtitle clarifies the column scopes ("range" vs "lifetime") so
      //    users don't compare the table against the wrong KPI (e.g. the
      //    109 lifetime "Total Posts" tile vs the 25 "Scheduled in range").
      //  • Pretty platform names (X, LinkedIn, TikTok, YouTube, WordPress,
      //    Website, Blog) instead of capitalized lowercase.
      //  • Blog row gets a tooltip explaining it combines WordPress + Website
      //    (Phase 39 will split them once schema gains a publish_target column).
      //  • Defensive footer: if the breakdown sum < KPI for any reason,
      //    surface the gap so users see "Showing 23 of 25" instead of
      //    silently doubting the math.
      const _PLATFORM_LABELS = {
        x: 'X',
        twitter: 'X',
        facebook: 'Facebook',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        tiktok: 'TikTok',
        youtube: 'YouTube',
        pinterest: 'Pinterest',
        reddit: 'Reddit',
        wordpress: 'WordPress',
        website: 'Website',
        blog: 'Blog',
      };
      const prettyPlatform = (p) => _PLATFORM_LABELS[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : '\u2014');
      const breakdownScheduledSum = breakdownByPlatform.reduce((s, r) => s + (r.scheduled || 0), 0);
      const kpiScheduled = (kpis && typeof kpis.scheduled === 'number') ? kpis.scheduled : breakdownScheduledSum;
      const breakdownGap = kpiScheduled - breakdownScheduledSum;
      const rangeLabel = this.state.range === '7d' ? 'last 7 days' : this.state.range === '90d' ? 'last 90 days' : 'last 30 days';
      contentHtml += `
         <div class="analytics-card" data-testid="analytics-platform-breakdown">
           <div class="analytics-card-header">
             <h3 class="analytics-card-title">Platform Breakdown</h3>
             <p class="analytics-card-subtitle" style="font-size:11px;color:var(--text-muted);margin:4px 0 0;">Scheduled in the ${rangeLabel} \u00b7 total drafts in your library</p>
           </div>
           ${breakdownByPlatform.length === 0 ? `
             <div style="padding:24px 16px;text-align:center;color:var(--text-muted);font-size:13px;">
               No scheduled posts or drafts yet for this brand. Generate content from the Studio to populate this view.
             </div>
           ` : `
             <div class="analytics-table-wrap">
               <table class="analytics-table">
               <thead>
                 <tr><th class="analytics-th">Platform</th><th class="analytics-th analytics-th--right">Scheduled</th><th class="analytics-th analytics-th--right">Drafts</th></tr>
               </thead>
               <tbody>
                 ${breakdownByPlatform.map(bp => {
                   const name = prettyPlatform(bp.platform);
                   const isBlog = bp.platform === 'blog';
                   const tooltip = isBlog ? ' title="Combined WordPress + Website blog posts. Per-platform split coming soon."' : '';
                   const blogHint = isBlog ? ' <span style="font-size:10px;color:var(--text-muted);margin-left:4px;">(WP + Website)</span>' : '';
                   return `
                   <tr class="analytics-row"${tooltip}>
                     <td class="analytics-td">${name}${blogHint}</td>
                     <td class="analytics-td analytics-td--right">${bp.scheduled}</td>
                     <td class="analytics-td analytics-td--right">${bp.drafts}</td>
                   </tr>`;}).join('')}
               </tbody>
             </table>
             ${breakdownGap > 0 ? `
               <div style="padding:8px 12px;font-size:11px;color:var(--text-muted);border-top:1px solid var(--border);background:rgba(245,158,11,0.04);">
                 Showing ${breakdownScheduledSum} of ${kpiScheduled} scheduled posts. ${breakdownGap} on a platform not represented above \u2014 see <a href="#calendar" style="color:var(--accent);">Calendar</a> for the rest.
               </div>
             ` : ''}
           `}
         </div>
       `;
      contentHtml += `</div>`; // End Grid

      // Attention
      if (attention.length > 0) {
        contentHtml += `
             <div class="analytics-card analytics-attention analytics-section" data-testid="analytics-attention">
               <div class="analytics-card-header">
                 <h3 class="analytics-card-title">Attention Needed</h3>
               </div>
               <div class="analytics-attention-list">
                 ${attention.map(i => `
                   <div class="analytics-attention-row">
                     <div class="analytics-attention-left">
                       <div class="analytics-attention-title-row">
                         <span class="analytics-pill ${i.type === 'alert' ? 'analytics-pill--danger' : 'analytics-pill--warn'}">${i.type}</span>
                         <span class="analytics-attention-title">${i.title}</span>
                       </div>
                       <div class="analytics-attention-sub">${i.reason}</div>
                     </div>
                     <a href="${i.link}" class="analytics-link">Fix</a>
                   </div>
                 `).join('')}
               </div>
             </div>
           `;
      }
    }

    return headerHtml + overviewContentStart + contentHtml + overviewContentEnd;
  }

  // R6 Stage 4 — plain-language per-platform sync health ("Engagement
  // sources"). Renders nothing until at least one sync has recorded state,
  // so brands that never sync see no change.
  _renderSyncSourcesStrip() {
    const rows = this.state.perfSyncStatus || [];
    if (!rows.length) return '';
    const names = { meta: 'Facebook/Instagram', x: 'X', linkedin: 'LinkedIn', wordpress: 'WordPress', youtube: 'YouTube', pinterest: 'Pinterest', reddit: 'Reddit' };
    const pill = (r) => {
      const label = names[r.platform] || r.platform;
      if (r.last_sync_status === 'success') {
        const when = this._formatRelativeTime(r.last_sync_completed_at);
        const posts = r.posts_synced_count ? `, ${r.posts_synced_count} post${r.posts_synced_count === 1 ? '' : 's'}` : '';
        return `<span class="analytics-pill analytics-pill--info" title="Last update pulled ${when || 'recently'}${posts}">${this._esc(label)} · updated ${when || 'recently'}</span>`;
      }
      if (r.last_sync_status === 'auth_failed') {
        return `<span class="analytics-pill analytics-pill--danger" title="${this._esc(r.last_sync_error || '')}">${this._esc(label)} · needs reconnect in Settings</span>`;
      }
      if (r.last_sync_status === 'failed') {
        return `<span class="analytics-pill analytics-pill--warn" title="${this._esc(r.last_sync_error || '')}">${this._esc(label)} · having trouble updating</span>`;
      }
      if (r.last_sync_status === 'running') {
        return `<span class="analytics-pill" title="Sync in progress">${this._esc(label)} · updating now…</span>`;
      }
      return `<span class="analytics-pill" title="No sync recorded yet">${this._esc(label)} · not updated yet</span>`;
    };
    return `
      <div class="analytics-card" data-testid="engagement-sync-sources" style="padding:10px 14px;margin-bottom:14px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
        <span style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--text-muted);">Engagement sources</span>
        ${rows.map(pill).join(' ')}
      </div>
    `;
  }

  renderPerformanceTab() {
    const { perfLoading, perfError, perfData, perfTrends, perfPosts, perfPostsTotal, perfPostsPage, perfSort, perfSyncing, perfLastSyncedAt, perfNextAllowedAt, perfRefreshMessage, perfRefreshMessageKind } = this.state;
    // Phase 37 — Refresh Now UX bits.
    const lastSyncedLabel = this._formatRelativeTime(perfLastSyncedAt);
    const cooldownActive = perfNextAllowedAt && new Date(perfNextAllowedAt).getTime() > Date.now();
    // Re-render once the cooldown expires so the Refresh Now button re-enables
    // without the user having to interact with the page.
    clearTimeout(this._cooldownTimer);
    if (cooldownActive) {
      const waitMs = Math.max(1000, new Date(perfNextAllowedAt).getTime() - Date.now() + 250);
      this._cooldownTimer = setTimeout(() => { if (this.el) this.render(); }, waitMs);
    }
    const refreshDisabled = perfSyncing || cooldownActive;
    const refreshLabel = perfSyncing ? 'Refreshing...' : 'Refresh Now';
    const toastBg = perfRefreshMessageKind === 'success' ? 'rgba(34,197,94,0.10)'
                  : perfRefreshMessageKind === 'rate-limited' ? 'rgba(245,158,11,0.10)'
                  : perfRefreshMessageKind === 'error' ? 'rgba(239,68,68,0.10)' : '';
    const toastBorder = perfRefreshMessageKind === 'success' ? 'rgba(34,197,94,0.25)'
                      : perfRefreshMessageKind === 'rate-limited' ? 'rgba(245,158,11,0.25)'
                      : perfRefreshMessageKind === 'error' ? 'rgba(239,68,68,0.25)' : '';
    const toastColor = perfRefreshMessageKind === 'success' ? '#22c55e'
                     : perfRefreshMessageKind === 'rate-limited' ? '#f59e0b'
                     : perfRefreshMessageKind === 'error' ? '#ef4444' : '';

    // Section header with sync button
    let html = `
      <div class="analytics-section-header analytics-section" data-testid="perf-section">
        <div class="analytics-header-content">
          <h2 class="analytics-section-title">Engagement</h2>
          <p class="analytics-section-subtitle">Likes, comments, shares, impressions — how your published content performs</p>
        </div>
        <div class="analytics-toolbar" style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;">
          <div style="display:flex;gap:8px;align-items:center;">
            <button id="btn-perf-sync" class="analytics-button analytics-button--primary" ${refreshDisabled ? 'disabled' : ''} data-testid="perf-sync-button" title="${cooldownActive ? 'Recently refreshed — try again shortly' : 'Pull the latest engagement metrics from connected platforms'}">
              ${refreshLabel}
            </button>
            <button class="analytics-button analytics-button--secondary" data-action="export-pdf" data-testid="pdf-export-button">
              \u2193 Export PDF
            </button>
          </div>
          ${lastSyncedLabel ? `
            <div style="font-size:11px;color:var(--text-muted);">Last refreshed ${lastSyncedLabel} \u00b7 auto-refreshes hourly</div>
          ` : `
            <div style="font-size:11px;color:var(--text-muted);">Auto-refreshes hourly. Click Refresh Now for the latest data.</div>
          `}
          ${perfRefreshMessage ? `
            <div style="font-size:11px;padding:4px 10px;border-radius:6px;background:${toastBg};border:1px solid ${toastBorder};color:${toastColor};">${perfRefreshMessage}</div>
          ` : ''}
        </div>
      </div>
      <div class="analytics-platform-filter-chips" data-testid="engagement-platform-chips">
        <button type="button" class="platform-filter-chip ${this.state.platform === 'website' ? 'platform-filter-chip--active' : ''}" data-site-platform="website" data-testid="engagement-chip-website">Website</button>
        <button type="button" class="platform-filter-chip ${this.state.platform === 'blog' ? 'platform-filter-chip--active' : ''}" data-site-platform="blog" data-testid="engagement-chip-blog">Blog</button>
      </div>
      ${this._renderSyncSourcesStrip()}
    `;

    if (perfLoading && !perfData) {
      html += `
        <div class="analytics-kpi-grid" data-testid="perf-loading-skeletons">
          ${Array(4).fill(0).map(() => '<div class="analytics-skeleton analytics-skeleton-card"></div>').join('')}
        </div>
        <div class="analytics-card"><div class="analytics-skeleton analytics-skeleton-chart"></div></div>
      `;
      return html;
    }

    if (perfError) {
      html += `
        <div class="analytics-banner analytics-banner--error" data-testid="perf-error-banner">
          <p class="analytics-banner-message">${this._esc(perfError)}</p>
          <button id="btn-retry-perf" class="analytics-button analytics-button--secondary analytics-button--sm">Retry</button>
        </div>
      `;
      return html;
    }

    if (!perfData) {
      html += `
        <div class="analytics-empty-state" data-testid="perf-empty">
          <div class="analytics-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--primary,#8B5CF6);opacity:0.6;">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <h4 class="analytics-empty-title">Connect your platforms to see engagement data</h4>
          <p class="analytics-empty-subtitle">Heist pulls likes, comments, shares, and reach every hour. Once a platform is connected we'll start collecting — usually visible within 24 hours.</p>
          <div class="empty-state-actions" style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-top:14px;">
            <a href="#settings?section=section-integrations" class="btn btn-primary btn-sm">Connect a platform &rarr;</a>
            <a href="#memory?tab=chat&prompt=Help%20me%20decide%20which%20social%20platform%20to%20focus%20on%20first%20for%20my%20brand" class="btn btn-secondary btn-sm" data-testid="empty-talk-to-heist">Talk to Heist</a>
          </div>
        </div>
      `;
      return html;
    }

    const { kpis, topPost, byPlatform, lastSyncedAt, sync_status } = perfData;
    // Coerce before formatting — website/blog tracked-site entries (and any
    // sparse rows) can carry undefined metric fields; without this the UI
    // renders the literal string "undefined".
    const fmt = (raw) => {
      const n = Number(raw) || 0;
      return n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'K' : String(n);
    };

    // Phase 31D: Last-synced hint above KPI row + muted partial-sync subtitle.
    if (lastSyncedAt) {
      const rel = this._relativeTime(lastSyncedAt);
      html += `<div class="engagement-last-synced" data-testid="engagement-last-synced">Last synced: ${rel}</div>`;
    }
    if (sync_status === 'partial') {
      html += `<div class="engagement-sync-partial-note" data-testid="engagement-sync-partial-note">Some platforms returned partial data (e.g., LinkedIn personal-scope shares unavailable)</div>`;
    }

    // Phase 31.5C: When the Website or Blog platform filter is active, show a
    // site-specific KPI row instead of the social KPI cards.
    const sitePlatformEntry = (byPlatform || []).find(p =>
      (this.state.platform === 'website' && p.platform === 'website') ||
      (this.state.platform === 'blog' && p.platform === 'blog')
    );
    if (sitePlatformEntry) {
      html += this._renderSitePlatformKpis(sitePlatformEntry, fmt);
      // Keep the rest of the tab (post leaderboard / platform breakdown / trend)
      // rendering after the site row — users who track both types can still see
      // social data below when they clear the site chip.
    }

    // KPI Cards
    html += `
      <div class="analytics-kpi-grid" data-testid="perf-kpi-grid">
        <div class="analytics-card analytics-kpi-card" data-testid="kpi-card-impressions">
          <div class="analytics-kpi-label">Impressions</div>
          <div class="analytics-kpi-value">${fmt(kpis.totalImpressions)}</div>
          <div class="analytics-kpi-subtext">${kpis.postCount} posts tracked</div>
        </div>
        <div class="analytics-card analytics-kpi-card" data-testid="kpi-card-engagement">
          <div class="analytics-kpi-label">Avg Engagement Rate</div>
          <div class="analytics-kpi-value">${kpis.avgEngagementRate.toFixed(2)}%</div>
          <div class="analytics-kpi-subtext">${fmt(kpis.totalLikes)} likes, ${fmt(kpis.totalComments)} comments</div>
        </div>
        <div class="analytics-card analytics-kpi-card" data-testid="kpi-card-reach">
          <div class="analytics-kpi-label">Total Reach</div>
          <div class="analytics-kpi-value">${fmt(kpis.totalReach)}</div>
          <div class="analytics-kpi-subtext">${fmt(kpis.totalShares)} shares, ${fmt(kpis.totalClicks)} clicks</div>
        </div>
        <div class="analytics-card analytics-kpi-card" data-testid="kpi-card-top-post">
          <div class="analytics-kpi-label">Top Post</div>
          <div class="analytics-kpi-value" style="font-size:1rem;line-height:1.4">${topPost ? this._truncate(topPost.text, 60) : 'No data yet'}</div>
          ${topPost ? `<div class="analytics-kpi-subtext analytics-kpi-subtext--success">${topPost.platform} &middot; ${fmt(topPost.impressions)} impr &middot; ${topPost.engagementRate}% eng</div>` : ''}
        </div>
      </div>
    `;

    // Trend Chart
    if (perfTrends && perfTrends.length >= 2) {
      html += `
        <div class="analytics-card" data-testid="perf-trend-chart">
          <div class="analytics-card-header"><h3 class="analytics-card-title">Engagement Trend</h3></div>
          <div class="analytics-chart analytics-chart-container">
            ${this.renderPerfChart(perfTrends)}
          </div>
        </div>
      `;
    } else {
      html += `
        <div class="analytics-card">
          <div class="analytics-card-header"><h3 class="analytics-card-title">Engagement Trend</h3></div>
          <div class="analytics-empty-state">
            <h4 class="analytics-empty-title">Not enough data for a trend chart</h4>
            <p class="analytics-empty-subtitle">Sync your platforms to start tracking daily engagement.</p>
          </div>
        </div>
      `;
    }

    // Two-column: Post Leaderboard + Platform Breakdown
    html += `<div class="analytics-two-col analytics-two-col--spaced">`;

    // Post Leaderboard
    html += `
      <div class="analytics-card" data-testid="perf-posts-table">
        <div class="analytics-card-header">
          <h3 class="analytics-card-title">Post Leaderboard</h3>
          <select id="perf-sort" class="analytics-select analytics-select--sm">
            <option value="engagement" ${perfSort === 'engagement' ? 'selected' : ''}>Top Engagement</option>
            <option value="impressions" ${perfSort === 'impressions' ? 'selected' : ''}>Most Impressions</option>
            <option value="likes" ${perfSort === 'likes' ? 'selected' : ''}>Most Likes</option>
            <option value="recent" ${perfSort === 'recent' ? 'selected' : ''}>Most Recent</option>
          </select>
        </div>
        <div class="analytics-table-wrap">
    `;

    if (perfPosts && perfPosts.length > 0) {
      html += `
          <table class="analytics-table">
            <thead>
              <tr>
                <th class="analytics-th">Post</th>
                <th class="analytics-th analytics-th--right">Impr</th>
                <th class="analytics-th analytics-th--right">Likes</th>
                <th class="analytics-th analytics-th--right">Eng%</th>
              </tr>
            </thead>
            <tbody>
              ${perfPosts.map(p => `
                <tr class="analytics-row post-engagement-row" data-post-id="${this._esc(String(p.id))}" data-testid="engagement-post-row">
                  <td class="analytics-td">
                    <span class="analytics-pill analytics-pill--info" style="margin-right:6px">${this._esc(p.platform)}</span>
                    ${this._truncate(p.text, 50)}
                  </td>
                  <td class="analytics-td analytics-td--right">${fmt(p.impressions)}</td>
                  <td class="analytics-td analytics-td--right">${fmt(p.likes)}</td>
                  <td class="analytics-td analytics-td--right">${p.engagementRate.toFixed(1)}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
      `;

      // Pagination
      const totalPages = Math.ceil(perfPostsTotal / 20);
      if (totalPages > 1) {
        html += `
          <div class="analytics-pagination">
            <button id="btn-perf-prev" class="analytics-button analytics-button--secondary analytics-button--sm" ${perfPostsPage <= 1 ? 'disabled' : ''}>Prev</button>
            <span class="analytics-page-info">Page ${perfPostsPage} of ${totalPages}</span>
            <button id="btn-perf-next" class="analytics-button analytics-button--secondary analytics-button--sm" ${perfPostsPage >= totalPages ? 'disabled' : ''}>Next</button>
          </div>
        `;
      }
    } else {
      html += `
        <div class="analytics-empty-state">
          <h4 class="analytics-empty-title">No post data yet</h4>
          <p class="analytics-empty-subtitle">Publish content and sync to see per-post metrics.</p>
        </div>
      `;
    }

    html += `</div></div>`;

    // Platform Breakdown — exclude website/blog tracked-site entries. They
    // have a different shape (pageviews/uptime, no impressions/likes) and are
    // rendered by _renderSitePlatformKpis; putting them through the social
    // columns produced "undefined" and "NaN%" cells.
    const socialByPlatform = (byPlatform || []).filter(p => !p.tracking_mode);
    html += `
      <div class="analytics-card" data-testid="perf-platform-breakdown">
        <div class="analytics-card-header"><h3 class="analytics-card-title">By Platform</h3></div>
        <div class="analytics-table-wrap">
    `;

    if (socialByPlatform.length > 0) {
      html += `
          <table class="analytics-table">
            <thead>
              <tr>
                <th class="analytics-th">Platform</th>
                <th class="analytics-th analytics-th--right">Impr</th>
                <th class="analytics-th analytics-th--right">Likes</th>
                <th class="analytics-th analytics-th--right">Eng%</th>
                <th class="analytics-th analytics-th--right">Posts</th>
              </tr>
            </thead>
            <tbody>
              ${socialByPlatform.map(p => {
                const pName = p.platform === 'x' ? 'X' : p.platform.charAt(0).toUpperCase() + p.platform.slice(1);
                return `
                <tr class="analytics-row">
                  <td class="analytics-td analytics-td--cap">${pName}</td>
                  <td class="analytics-td analytics-td--right">${fmt(p.impressions)}</td>
                  <td class="analytics-td analytics-td--right">${fmt(p.likes)}</td>
                  <td class="analytics-td analytics-td--right">${(Math.round((p.avgEngagementRate || 0) * 10000) / 100).toFixed(1)}%</td>
                  <td class="analytics-td analytics-td--right">${p.postCount || 0}</td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
      `;
    } else {
      html += `<div class="analytics-empty-state"><h4 class="analytics-empty-title">No platform data</h4></div>`;
    }

    html += `</div></div>`;
    html += `</div>`; // close two-col

    // Last synced
    if (lastSyncedAt) {
      html += `<div class="analytics-sync-footer" data-testid="perf-last-synced">Last synced: ${new Date(lastSyncedAt).toLocaleString()}</div>`;
    }

    // Phase 31E: Per-post drill-down drawer
    html += this._renderEngagementDrawer();

    return html;
  }

  // Phase 31E: Build the drill-down drawer HTML from state.perfDrawerPost.
  // Read-only — no action buttons, no extra fetch. Uses row data already in memory.
  _renderEngagementDrawer() {
    const post = this.state.perfDrawerPost;
    if (!post) return '';

    const fmt = (n) => n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'K' : String(n || 0);

    const platformUrl = this._platformPostUrl(post);
    const synced = post.fetchedAt ? new Date(post.fetchedAt).toLocaleString() : '—';

    return `
      <div class="engagement-drawer-backdrop" data-engagement-drawer-close></div>
      <aside class="engagement-drawer" data-testid="engagement-drawer" role="dialog" aria-label="Post engagement details">
        <div class="engagement-drawer-header">
          <div class="engagement-drawer-header-meta">
            <span class="analytics-pill analytics-pill--info">${this._esc(post.platform)}</span>
            <span class="engagement-drawer-preview">${this._truncate(post.text || '', 200)}</span>
          </div>
          <button type="button" class="engagement-drawer-close" data-engagement-drawer-close data-testid="engagement-drawer-close" aria-label="Close drawer">&times;</button>
        </div>
        <div class="engagement-rate-hero">${(post.engagementRate || 0).toFixed(2)}%</div>
        <div class="engagement-rate-vs-avg">Engagement rate</div>
        <div class="engagement-drawer-metrics">
          <div class="engagement-metric-cell"><div class="engagement-metric-value">${fmt(post.impressions)}</div><div class="engagement-metric-label">Impressions</div></div>
          <div class="engagement-metric-cell"><div class="engagement-metric-value">${fmt(post.reach)}</div><div class="engagement-metric-label">Reach</div></div>
          <div class="engagement-metric-cell"><div class="engagement-metric-value">${fmt(post.likes)}</div><div class="engagement-metric-label">Likes</div></div>
          <div class="engagement-metric-cell"><div class="engagement-metric-value">${fmt(post.comments)}</div><div class="engagement-metric-label">Comments</div></div>
          <div class="engagement-metric-cell"><div class="engagement-metric-value">${fmt(post.shares)}</div><div class="engagement-metric-label">Shares</div></div>
          <div class="engagement-metric-cell"><div class="engagement-metric-value">${fmt(post.clicks)}</div><div class="engagement-metric-label">Clicks</div></div>
        </div>
        <div class="engagement-drawer-footer">
          <div class="engagement-drawer-timestamps">
            <div><strong>Last synced:</strong> ${synced}</div>
          </div>
          ${platformUrl ? `<a class="engagement-drawer-link" href="${platformUrl}" target="_blank" rel="noopener noreferrer" data-testid="engagement-drawer-link">View on Platform</a>` : ''}
        </div>
      </aside>
    `;
  }

  // Phase 31E: Build a platform-specific URL from the stored external_post_id.
  // Facebook: https://www.facebook.com/{id}
  // Instagram: https://www.instagram.com/p/{id}/
  // X: https://x.com/i/web/status/{id}
  // LinkedIn: https://www.linkedin.com/feed/update/{urn}/
  // Blog: external_post_id holds the full URL (wired by the WP fetcher in 31C+)
  _platformPostUrl(post) {
    if (!post || !post.external_post_id) return null;
    const id = post.external_post_id;
    switch ((post.platform || '').toLowerCase()) {
      case 'facebook':  return `https://www.facebook.com/${id}`;
      case 'instagram': return `https://www.instagram.com/p/${id}/`;
      case 'x':         return `https://x.com/i/web/status/${id}`;
      case 'linkedin': {
        const urn = id.startsWith('urn:') ? id : `urn:li:share:${id}`;
        return `https://www.linkedin.com/feed/update/${urn}/`;
      }
      case 'blog':      return /^https?:\/\//.test(id) ? id : null;
      default:          return null;
    }
  }

  async fetchTeamStats() {
    this.setState({ teamLoading: true, teamError: null });
    try {
      const res = await client.getTeamStats({ range: this.state.range });
      if (!res.ok) throw new Error(res.error || 'Failed to load team stats');
      this.setState({ teamLoading: false, teamStats: res });
    } catch (err) {
      this.setState({ teamLoading: false, teamError: err.message });
    }
  }

  renderTeamTab() {
    const { teamLoading, teamError, teamStats } = this.state;

    let html = `
      <div class="analytics-section-header analytics-section">
        <div class="analytics-header-content">
          <h2 class="analytics-section-title">Team Performance</h2>
          <p class="analytics-section-subtitle">Who's creating, editing, and reviewing content across your workspace</p>
        </div>
      </div>
    `;

    if (teamLoading) {
      html += `<div class="analytics-kpi-grid">${Array(3).fill(0).map(() => '<div class="analytics-skeleton analytics-skeleton-card"></div>').join('')}</div>`;
      return html;
    }
    if (teamError) {
      html += `<div class="analytics-banner analytics-banner--error"><p class="analytics-banner-message">${this._esc(teamError)}</p></div>`;
      return html;
    }
    if (!teamStats?.members?.length) {
      html += `<div class="analytics-empty-state"><h4 class="analytics-empty-title">No team activity yet</h4><p class="analytics-empty-subtitle">Activity will appear here once team members start creating content.</p></div>`;
      return html;
    }

    const members = teamStats.members;
    const totalCreated = members.reduce((s, m) => s + m.contentCreated, 0);
    const mostActive = members[0];

    html += `
      <div class="analytics-kpi-grid">
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Team Members</div>
          <div class="analytics-kpi-value">${members.length}</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Content Created</div>
          <div class="analytics-kpi-value">${totalCreated}</div>
          <div class="analytics-kpi-subtext">Last ${teamStats.rangeDays} days</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Most Active</div>
          <div class="analytics-kpi-value" style="font-size:1.1rem;">${this._esc(mostActive?.name || '\u2014')}</div>
          <div class="analytics-kpi-subtext">${mostActive?.totalActions || 0} actions</div>
        </div>
      </div>
    `;

    html += `
      <div class="analytics-card">
        <div class="analytics-card-header"><h3 class="analytics-card-title">Member Activity</h3></div>
        <div class="analytics-table-wrap">
          <table class="analytics-table">
            <thead>
              <tr>
                <th class="analytics-th">Member</th>
                <th class="analytics-th">Role</th>
                <th class="analytics-th analytics-th--right">Created</th>
                <th class="analytics-th analytics-th--right">Edited</th>
                <th class="analytics-th analytics-th--right">Reviewed</th>
                <th class="analytics-th analytics-th--right">Brands</th>
                <th class="analytics-th analytics-th--right">Last Active</th>
              </tr>
            </thead>
            <tbody>
              ${members.map(m => `
                <tr class="analytics-row">
                  <td class="analytics-td">
                    <div style="font-weight:600;">${this._esc(m.name)}</div>
                    <div style="font-size:11px;color:var(--text-muted);">${this._esc(m.email || '')}</div>
                  </td>
                  <td class="analytics-td"><span class="role-badge role-badge--${m.role}">${m.role}</span></td>
                  <td class="analytics-td analytics-td--right">${m.contentCreated}</td>
                  <td class="analytics-td analytics-td--right">${m.contentEdited}</td>
                  <td class="analytics-td analytics-td--right">${m.approvalsReviewed}</td>
                  <td class="analytics-td analytics-td--right">${m.brandAccess}</td>
                  <td class="analytics-td analytics-td--right" style="font-size:11px;color:var(--text-muted);">${m.lastActive ? new Date(m.lastActive).toLocaleDateString() : '\u2014'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    return html;
  }

  // Phase 31D: "Coming soon" toast for the Website + Enhanced-Blog chips.
  // Website analytics + enhanced Blog (Jetpack / tracking-pixel views) ship in Phase 31.5.
  // Phase 31.5C: Polymorphic KPI row for Website/Blog entries.
  // Pixel mode shows pageviews/unique visitors; url_monitor mode shows uptime
  // + response time + latest-post date.
  _renderSitePlatformKpis(entry, fmt) {
    const isPixel = entry.tracking_mode === 'pixel';
    const label = entry.platform === 'blog' ? 'Blog' : 'Website';
    const sourceClass = isPixel ? 'data-source-indicator--pixel' : 'data-source-indicator--monitor';
    const rowClass = entry.platform === 'blog' ? 'blog-kpi-row' : 'website-kpi-row';
    const lastPostRel = entry.last_seen_post_date ? this._relativeTime(entry.last_seen_post_date) : '—';

    let cardsHtml;
    if (isPixel) {
      cardsHtml = `
        <div class="analytics-card analytics-kpi-card" data-testid="site-kpi-pageviews">
          <div class="analytics-kpi-label">Pageviews (${this.state.range === '7d' ? '7d' : this.state.range === '90d' ? '90d' : '30d'})</div>
          <div class="analytics-kpi-value">${fmt(entry.pageviews || 0)}</div>
          <div class="analytics-kpi-subtext">${entry.sites || 0} site${(entry.sites || 0) === 1 ? '' : 's'}</div>
        </div>
        <div class="analytics-card analytics-kpi-card" data-testid="site-kpi-unique-visitors">
          <div class="analytics-kpi-label">Unique Visitors</div>
          <div class="analytics-kpi-value">${fmt(entry.unique_visitors || 0)}</div>
          <div class="analytics-kpi-subtext">Session-hash based</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">${entry.platform === 'blog' ? 'Top Article' : 'Top Page'}</div>
          <div class="analytics-kpi-value" style="font-size:1rem;line-height:1.4">${this._esc(entry.top_path || '—')}</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Top Referrer</div>
          <div class="analytics-kpi-value" style="font-size:1rem;line-height:1.4">${this._esc(entry.top_referrer || '—')}</div>
        </div>
      `;
    } else {
      const uptime = entry.uptime_pct != null ? Number(entry.uptime_pct).toFixed(1) : '—';
      cardsHtml = `
        <div class="analytics-card analytics-kpi-card" data-testid="site-kpi-uptime">
          <div class="analytics-kpi-label">Uptime</div>
          <div class="analytics-kpi-value">${uptime}%</div>
          <div class="website-uptime-bar"><div class="website-uptime-bar-fill" style="width:${typeof entry.uptime_pct === 'number' ? entry.uptime_pct : 0}%"></div></div>
        </div>
        <div class="analytics-card analytics-kpi-card" data-testid="site-kpi-response">
          <div class="analytics-kpi-label">Avg Response</div>
          <div class="analytics-kpi-value">${entry.avg_response_ms != null ? entry.avg_response_ms + 'ms' : '—'}</div>
        </div>
        <div class="analytics-card analytics-kpi-card" data-testid="site-kpi-last-post">
          <div class="analytics-kpi-label">Latest Post</div>
          <div class="analytics-kpi-value" style="font-size:1rem;line-height:1.4">${lastPostRel}</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Sites Tracked</div>
          <div class="analytics-kpi-value">${entry.sites || 0}</div>
        </div>
      `;
    }

    return `
      <div class="analytics-section-header analytics-section" data-testid="site-platform-section">
        <div class="analytics-header-content">
          <h3 class="analytics-card-title" style="margin:0;font-size:15px;">${label} analytics</h3>
          <span class="data-source-indicator ${sourceClass}" data-testid="site-data-source">Data: ${entry.data_source}</span>
        </div>
      </div>
      <div class="analytics-kpi-grid ${rowClass}" data-testid="site-kpi-grid">${cardsHtml}</div>
    `;
  }

  // Phase 31D: Human-readable relative time for the "Last synced" hint.
  _relativeTime(iso) {
    try {
      const then = new Date(iso).getTime();
      if (isNaN(then)) return new Date(iso).toLocaleString();
      const diffSec = Math.max(0, (Date.now() - then) / 1000);
      if (diffSec < 60) return 'just now';
      if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minutes ago`;
      if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`;
      return new Date(iso).toLocaleDateString();
    } catch (_) { return String(iso); }
  }

  _showComingSoonToast() {
    const existing = document.querySelector('[data-testid="engagement-coming-soon-toast"]');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'analytics-toast analytics-toast--info';
    toast.setAttribute('data-testid', 'engagement-coming-soon-toast');
    toast.textContent = 'Website and Blog analytics ship in Phase 31.5 — stay tuned';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }

  _esc(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  _truncate(text, max) {
    if (!text) return '<em>No text</em>';
    // Truncate the RAW text first, then escape — slicing after escaping can
    // cut an HTML entity in half (e.g. "&amp;" → "&am...").
    const raw = String(text);
    const cut = raw.length > max ? raw.slice(0, max) + '...' : raw;
    return this._esc(cut);
  }

  renderPerfChart(series) {
    const w = 800, h = 300, pad = 40;
    const maxImpr = Math.max(...series.map(s => s.impressions), 1) * 1.1;
    const maxEng = Math.max(...series.map(s => s.likes + s.comments + s.shares), 1) * 1.1;

    const imprPoints = series.map((s, i) => {
      const x = pad + (i / (series.length - 1)) * (w - pad * 2);
      const y = h - pad - (s.impressions / maxImpr) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');

    const engPoints = series.map((s, i) => {
      const x = pad + (i / (series.length - 1)) * (w - pad * 2);
      const total = s.likes + s.comments + s.shares;
      const y = h - pad - (total / maxEng) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');

    // ANLX-P1 — gradient area fill under the impressions line + soft gridlines
    // for a more premium read. Same data, same classes on lines/points.
    const gridLines = [0.25, 0.5, 0.75].map(f => {
      const gy = pad + f * (h - pad * 2);
      return `<line class="analytics-chart-grid" x1="${pad}" y1="${gy}" x2="${w - pad}" y2="${gy}" />`;
    }).join('');
    const areaPoints = `${pad},${h - pad} ${imprPoints} ${w - pad},${h - pad}`;

    return `
      <svg viewBox="0 0 ${w} ${h}" class="analytics-svg-chart">
        <defs>
          <linearGradient id="anlx-perf-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="var(--a-primary, #4f46e5)" stop-opacity="0.22"/>
            <stop offset="100%" stop-color="var(--a-primary, #4f46e5)" stop-opacity="0"/>
          </linearGradient>
        </defs>
        ${gridLines}
        <polygon class="analytics-chart-area" points="${areaPoints}" fill="url(#anlx-perf-grad)" />
        <polyline class="analytics-chart-line" points="${imprPoints}" vector-effect="non-scaling-stroke" />
        <polyline class="analytics-chart-line analytics-chart-line--secondary" points="${engPoints}" vector-effect="non-scaling-stroke" />
        ${series.map((s, i) => {
          const x = pad + (i / (series.length - 1)) * (w - pad * 2);
          const y = h - pad - (s.impressions / maxImpr) * (h - pad * 2);
          return `<circle class="analytics-chart-point" cx="${x}" cy="${y}" r="3"><title>${s.date}: ${s.impressions} impr</title></circle>`;
        }).join('')}
      </svg>
      <div class="analytics-chart-legend" style="justify-content:center;gap:20px;margin-top:8px">
        <span style="display:inline-flex;align-items:center;gap:4px"><span style="width:16px;height:3px;background:var(--primary,#8B5CF6);border-radius:2px;display:inline-block"></span> Impressions</span>
        <span style="display:inline-flex;align-items:center;gap:4px"><span style="width:16px;height:3px;background:var(--success,#10b981);border-radius:2px;display:inline-block"></span> Engagement</span>
      </div>
    `;
  }

  renderContentStats() {
    const { contentStatsLoading, contentStatsError, contentStats } = this.state;

    let html = `
      <div class="analytics-section-header analytics-section">
        <div class="analytics-header-content">
          <h2 class="analytics-section-title">Content Production</h2>
          <p class="analytics-section-subtitle">Posts created, platform mix, and weekly trends</p>
        </div>
      </div>
    `;

    if (contentStatsLoading && !contentStats) {
      html += `
        <div class="analytics-kpi-grid">
          ${Array(4).fill(0).map(() => '<div class="analytics-skeleton analytics-skeleton-card"></div>').join('')}
        </div>
      `;
      return html;
    }

    if (contentStatsError) {
      html += `<div class="analytics-banner analytics-banner--error"><p class="analytics-banner-message">${this._esc(contentStatsError)}</p></div>`;
      return html;
    }

    if (!contentStats) return html;

    const { totals, postsByDay, postsByPlatform, bestDay, thisWeek, lastWeek, weekOverWeekChange } = contentStats;
    const t = totals || { total: 0, published: 0, drafts: 0, scheduled: 0 };

    // KPI Cards
    const wowLabel = weekOverWeekChange !== null ? `${weekOverWeekChange >= 0 ? '+' : ''}${weekOverWeekChange}% vs last week` : 'No prior week data';
    const wowStatus = weekOverWeekChange !== null ? (weekOverWeekChange >= 0 ? 'success' : 'warn') : '';

    html += `
      <div class="analytics-kpi-grid">
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Total Posts</div>
          <div class="analytics-kpi-value">${t.total}</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Published</div>
          <div class="analytics-kpi-value">${t.published || 0}</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">This Week</div>
          <div class="analytics-kpi-value">${thisWeek}</div>
          <div class="analytics-kpi-subtext ${wowStatus === 'success' ? 'analytics-kpi-subtext--success' : wowStatus === 'warn' ? 'analytics-kpi-subtext--warn' : ''}">${wowLabel}</div>
        </div>
        <div class="analytics-card analytics-kpi-card">
          <div class="analytics-kpi-label">Best Day</div>
          <div class="analytics-kpi-value">${bestDay || '—'}</div>
          <div class="analytics-kpi-subtext">Most active creation day</div>
        </div>
      </div>
    `;

    // 30-day bar chart + platform breakdown
    html += `<div class="analytics-two-col">`;

    // Posts by day chart (last 30 days)
    if (postsByDay && postsByDay.length > 0) {
      const maxCount = Math.max(...postsByDay.map(d => d.count), 1);
      html += `
        <div class="analytics-card">
          <div class="analytics-card-header"><h3 class="analytics-card-title">Posts Created (Last 30 Days)</h3></div>
          <div class="analytics-chart analytics-chart--coverage">
            <div class="analytics-coverage-bars">
              ${postsByDay.map(d => {
                const pct = Math.min(100, (d.count / maxCount) * 100);
                return `<div class="analytics-coverage-bar" style="height:${pct}%" title="${d.date}: ${d.count} posts"></div>`;
              }).join('')}
            </div>
          </div>
          <div class="analytics-chart-legend">
            <span>${postsByDay[0]?.date || ''}</span>
            <span>${postsByDay[postsByDay.length - 1]?.date || ''}</span>
          </div>
        </div>
      `;
    } else {
      html += `
        <div class="analytics-card">
          <div class="analytics-card-header"><h3 class="analytics-card-title">Posts Created (Last 30 Days)</h3></div>
          <div class="analytics-empty-state"><div class="analytics-empty-icon"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--primary,#8B5CF6);opacity:0.6;"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></div><h4 class="analytics-empty-title">Start creating content to see your planning analytics</h4><p class="analytics-empty-subtitle"><a href="#create" class="btn btn-primary btn-sm" style="margin-top:8px;">Open Content Studio &rarr;</a></p></div>
        </div>
      `;
    }

    // Platform breakdown
    html += `
      <div class="analytics-card">
        <div class="analytics-card-header"><h3 class="analytics-card-title">Posts by Platform</h3></div>
        <div class="analytics-table-wrap">
          <table class="analytics-table">
            <thead><tr><th class="analytics-th">Platform</th><th class="analytics-th analytics-th--right">Count</th></tr></thead>
            <tbody>
              ${(postsByPlatform || []).map(p => {
                const pName = p.platform === 'x' ? 'X' : p.platform.charAt(0).toUpperCase() + p.platform.slice(1);
                return `
                <tr class="analytics-row">
                  <td class="analytics-td">${pName}</td>
                  <td class="analytics-td analytics-td--right">${p.count}</td>
                </tr>`;
              }).join('') || '<tr><td class="analytics-td" colspan="2">No data</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>
    `;

    html += `</div>`;
    return html;
  }

  renderFollowerGrowth() {
    const { followersLoading, followersError, followersData, followersRange, followersProvider, snapshotRunning, lastRunResults, followersRunError } = this.state;

    // Determine state for testing
    const sectionState = (!followersLoading && !followersError) ? 'ready' : 'loading';

    // Header with Controls
    let html = `
      <div class="analytics-section-header analytics-section" id="follower-growth-section" data-testid="followers-section" data-state="${sectionState}">
         <div class="analytics-header-content">
            <h2 class="analytics-section-title">Followers</h2>
            <p class="analytics-section-subtitle">Track audience size over time</p>
         </div>
         <div class="analytics-toolbar">
            <div class="analytics-control">
              <select id="analytics-followers-range" class="analytics-select">
                 <option value="7d" ${followersRange === '7d' ? 'selected' : ''}>Last 7 Days</option>
                 <option value="30d" ${followersRange === '30d' ? 'selected' : ''}>Last 30 Days</option>
                 <option value="90d" ${followersRange === '90d' ? 'selected' : ''}>Last 90 Days</option>
              </select>
            </div>
            <div class="analytics-control">
              <select id="analytics-followers-provider" class="analytics-select">
                 <option value="all" ${followersProvider === 'all' ? 'selected' : ''}>All Platforms</option>
                 <option value="meta" ${followersProvider === 'meta' ? 'selected' : ''}>Meta (IG)</option>
                 <option value="x" ${followersProvider === 'x' ? 'selected' : ''}>X</option>
                 <option value="linkedin" ${followersProvider === 'linkedin' ? 'selected' : ''}>LinkedIn</option>
              </select>
            </div>
            
            <div class="analytics-divider" aria-hidden="true"></div>
            
            <button id="btn-run-snapshot" class="analytics-button analytics-button--primary" ${snapshotRunning ? 'disabled' : ''} data-testid="run-snapshot-button">
               ${snapshotRunning ? 'Running...' : 'Run Snapshot Now'}
            </button>
         </div>
      </div>
    `;

    // Error banner for run action
    if (followersRunError) {
      html += `
        <div class="analytics-banner analytics-banner--error" style="margin-top: 16px;">
           <span class="analytics-banner-message">${this._esc(followersRunError.message)}</span>
           ${followersRunError.requestId ? `<span class="analytics-request-id">Request ID: ${this._esc(followersRunError.requestId)}</span>` : ''}
        </div>
      `;
    }

    if (followersLoading && !followersData) {
      html += `
          <div class="analytics-kpi-grid analytics-kpi-grid--followers">
             <div class="analytics-card analytics-kpi-card">
               <div class="analytics-skeleton analytics-skeleton-card"></div>
             </div>
             <div class="analytics-card analytics-kpi-card">
                <div class="analytics-skeleton analytics-skeleton-card"></div>
             </div>
             <div class="analytics-card analytics-kpi-card">
                <div class="analytics-skeleton analytics-skeleton-card"></div>
             </div>
             <div class="analytics-card analytics-kpi-card">
                <div class="analytics-skeleton analytics-skeleton-card"></div>
             </div>
          </div>
          
          <div class="analytics-card">
            <div class="analytics-skeleton analytics-skeleton-chart"></div>
          </div>
          
          <div class="analytics-two-col analytics-two-col--spaced">
            <div class="analytics-card"><div class="analytics-skeleton analytics-skeleton-table"></div></div>
            <div class="analytics-card"><div class="analytics-skeleton analytics-skeleton-table"></div></div>
          </div>
        `;
      return html;
    }

    if (followersError) {
      html += `
          <div class="analytics-banner analytics-banner--error" style="margin-top: 16px;">
            <p class="analytics-banner-message">${this._esc(followersError.message)}</p>
            <button id="btn-retry-followers" class="analytics-button analytics-button--secondary analytics-button--sm">Retry</button>
          </div>
        `;
      return html;
    }

    if (followersData) {
      const { series, latest, growth, engagementByPlatform } = followersData;

      let totalFollowers = 0;
      let startFollowers = 0;

      const getLatestCount = (p) => latest[p]?.followers_count || 0;
      const getStartCount = (p) => {
        const s = series[p];
        if (!s || s.length === 0) return 0;
        return s[0].followers_count;
      };

      if (followersProvider === 'all') {
        ['meta', 'x', 'linkedin'].forEach(p => {
          totalFollowers += getLatestCount(p);
          startFollowers += getStartCount(p);
        });
      } else {
        totalFollowers = getLatestCount(followersProvider);
        startFollowers = getStartCount(followersProvider);
      }

      const netChange = totalFollowers - startFollowers;
      const netChangeSign = netChange >= 0 ? '+' : '';

      const netChangeLabel = followersRange === '7d' ? '7d' : (followersRange === '90d' ? '90d' : '30d');

      // Compute aggregate growth rate
      let growthRate = 0;
      if (growth) {
        if (followersProvider === 'all') {
          growthRate = startFollowers > 0 ? ((netChange / startFollowers) * 100) : 0;
        } else if (growth[followersProvider]) {
          growthRate = growth[followersProvider].rate;
        }
      }
      const growthRateSign = growthRate >= 0 ? '+' : '';

      // Aggregate engagement across selected providers
      let totalEngRate = 0;
      let totalEngPosts = 0;
      if (engagementByPlatform) {
        const providers = followersProvider === 'all' ? ['meta', 'x', 'linkedin'] : [followersProvider];
        let engSum = 0, engCount = 0;
        providers.forEach(p => {
          if (engagementByPlatform[p]) {
            engSum += engagementByPlatform[p].avgEngagementRate * engagementByPlatform[p].postCount;
            engCount += engagementByPlatform[p].postCount;
          }
        });
        totalEngRate = engCount > 0 ? (engSum / engCount) : 0;
        totalEngPosts = engCount;
      }

      // KPI Cards
      html += `
          <div class="analytics-kpi-grid analytics-kpi-grid--followers">
             <div class="analytics-card analytics-kpi-card" data-testid="kpi-card-total-followers">
                <div class="analytics-kpi-label">Total Followers</div>
                <div class="analytics-kpi-value" data-testid="kpi-value">${totalFollowers.toLocaleString()}</div>
             </div>
             <div class="analytics-card analytics-kpi-card">
                <div class="analytics-kpi-label">Net Change (${netChangeLabel})</div>
                <div class="analytics-kpi-value ${netChange < 0 ? 'analytics-kpi-subtext--danger' : (netChange > 0 ? 'analytics-kpi-subtext--success' : '')}">${netChangeSign}${netChange.toLocaleString()}</div>
             </div>
             <div class="analytics-card analytics-kpi-card">
                <div class="analytics-kpi-label">Growth Rate</div>
                <div class="analytics-kpi-value ${growthRate < 0 ? 'analytics-kpi-subtext--danger' : (growthRate > 0 ? 'analytics-kpi-subtext--success' : '')}">${growthRateSign}${growthRate.toFixed(1)}%</div>
                <div class="analytics-kpi-subtext">${netChangeLabel} period</div>
             </div>
             ${totalEngPosts > 0 ? `
             <div class="analytics-card analytics-kpi-card">
                <div class="analytics-kpi-label">Avg Engagement</div>
                <div class="analytics-kpi-value">${totalEngRate.toFixed(1)}%</div>
                <div class="analytics-kpi-subtext">${totalEngPosts} posts tracked</div>
             </div>
             ` : ''}
          </div>
        `;

      // Chart
      html += `
          <div class="analytics-card" data-testid="followers-chart-card">
             <div class="analytics-card-header"><h3 class="analytics-card-title">Trend</h3></div>
             <div class="analytics-chart analytics-chart-container analytics-followers-chart">
               ${this.renderChart(series, followersProvider)}
             </div>
          </div>
        `;

      // Tables
      html += `<div class="analytics-two-col analytics-two-col--spaced analytics-followers-tables">`;

      // Latest Snapshots
      html += `
          <div class="analytics-card" data-testid="latest-snapshots">
             <div class="analytics-card-header"><h3 class="analytics-card-title">Latest Snapshots</h3></div>
             <div class="analytics-table-wrap">
      `;

      let hasLatest = false;
      const latestRows = ['meta', 'x', 'linkedin'].map(p => {
        const l = latest[p];
        if (!l) return '';
        hasLatest = true;
        const g = growth && growth[p];
        const changeStr = g ? `${g.change >= 0 ? '+' : ''}${g.change.toLocaleString()}` : '—';
        const changeClass = g ? (g.change > 0 ? 'analytics-kpi-subtext--success' : g.change < 0 ? 'analytics-kpi-subtext--danger' : '') : '';
        const eng = engagementByPlatform && engagementByPlatform[p];
        const engStr = eng ? `${eng.avgEngagementRate.toFixed(1)}%` : '—';
        return `<tr class="analytics-row"><td class="analytics-td analytics-td--cap">${p}</td><td class="analytics-td analytics-td--right">${l.followers_count.toLocaleString()}</td><td class="analytics-td analytics-td--right ${changeClass}">${changeStr}</td><td class="analytics-td analytics-td--right">${engStr}</td></tr>`;
      }).join('');

      if (hasLatest) {
        html += `
               <table class="analytics-table">
                 <thead><tr><th class="analytics-th">Platform</th><th class="analytics-th analytics-th--right">Followers</th><th class="analytics-th analytics-th--right">Change</th><th class="analytics-th analytics-th--right">Eng%</th></tr></thead>
                 <tbody>
                    ${latestRows}
                 </tbody>
               </table>
        `;
      } else {
        html += `
            <div class="analytics-empty-state">
                <h4 class="analytics-empty-title">No snapshots yet</h4>
                <p class="analytics-empty-subtitle">Run a snapshot to start building history.</p>
            </div>
        `;
      }

      html += `
             </div>
          </div>
      `;

      // Last Run Results
      html += `
            <div class="analytics-card" data-testid="last-run-results">
              <div class="analytics-card-header"><h3 class="analytics-card-title">Last Run Results</h3></div>
              <div class="analytics-table-wrap">
      `;

      if (lastRunResults && lastRunResults.length > 0) {
        html += `
                 <table class="analytics-table">
                   <tbody>
                    ${lastRunResults.map(r => `
                      <tr class="analytics-row">
                        <td class="analytics-td analytics-td--cap">${this._esc(r.provider)}</td>
                        <td class="analytics-td analytics-td--right">
                          <span class="analytics-pill analytics-pill--info">${this._esc(r.status)}</span>
                        </td>
                      </tr>`).join('')}
                   </tbody>
                 </table>
          `;
      } else {
        html += `
            <div class="analytics-empty-state">
                <h4 class="analytics-empty-title">No recent runs</h4>
            </div>
          `;
      }

      html += `
              </div>
            </div>
          </div>
      `;
    }

    return html;
  }

  renderChart(series, provider) {
    let chartPoints = [];
    let allDates = new Set();
    const providers = provider === 'all' ? ['meta', 'x', 'linkedin'] : [provider];

    // O(n) performance improvement by mapping date -> followers_count
    const providerMaps = {};

    providers.forEach(p => {
      providerMaps[p] = {};
      series[p]?.forEach(pt => {
        allDates.add(pt.date);
        providerMaps[p][pt.date] = pt.followers_count;
      });
    });

    const sortedDates = Array.from(allDates).sort();

    sortedDates.forEach(date => {
      let total = 0;
      providers.forEach(p => {
        if (providerMaps[p][date] !== undefined) {
          total += providerMaps[p][date];
        }
      });
      chartPoints.push({ date, value: total });
    });

    if (chartPoints.length < 2) {
      return `
        <div class="analytics-empty-state">
           <h3 class="analytics-empty-title">No chart data yet</h3>
           <p class="analytics-empty-subtitle">${chartPoints.length === 0 ? '0 snapshots captured. ' : ''}Click <strong>Run Snapshot Now</strong> to capture your data. After 2+ snapshots, we’ll show the trend.</p>
        </div>
      `;
    }

    // Simple SVG Render
    const w = 800, h = 300, pad = 40;
    const max = Math.max(...chartPoints.map(p => p.value)) * 1.1;
    const min = Math.min(...chartPoints.map(p => p.value)) * 0.9;
    const range = max - min || 1;

    const points = chartPoints.map((p, i) => {
      const x = pad + (i / (chartPoints.length - 1)) * (w - pad * 2);
      const y = h - pad - ((p.value - min) / range) * (h - pad * 2);
      return `${x},${y}`;
    }).join(' ');

    // ANLX-P1 — gradient area fill + gridlines (matches the engagement chart).
    const gridLines = [0.25, 0.5, 0.75].map(f => {
      const gy = pad + f * (h - pad * 2);
      return `<line class="analytics-chart-grid" x1="${pad}" y1="${gy}" x2="${w - pad}" y2="${gy}" />`;
    }).join('');
    const areaPoints = `${pad},${h - pad} ${points} ${w - pad},${h - pad}`;

    return `
       <svg viewBox="0 0 ${w} ${h}" class="analytics-svg-chart">
          <defs>
            <linearGradient id="anlx-followers-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="var(--a-primary, #4f46e5)" stop-opacity="0.22"/>
              <stop offset="100%" stop-color="var(--a-primary, #4f46e5)" stop-opacity="0"/>
            </linearGradient>
          </defs>
          ${gridLines}
          <polygon class="analytics-chart-area" points="${areaPoints}" fill="url(#anlx-followers-grad)" />
          <polyline class="analytics-chart-line" points="${points}" vector-effect="non-scaling-stroke"/>
          ${chartPoints.map((p, i) => {
      const x = pad + (i / (chartPoints.length - 1)) * (w - pad * 2);
      const y = h - pad - ((p.value - min) / range) * (h - pad * 2);
      return `<circle class="analytics-chart-point" cx="${x}" cy="${y}" r="4"><title>${p.date}: ${p.value}</title></circle>`;
    }).join('')}
       </svg>
    `;
  }

}
